import React, { useState, useEffect, Fragment } from 'react';
import { Card, CardBody, Row, Col } from 'reactstrap';
import AlertIcon from 'mdi-react/AlertCircleOutlineIcon';
import CheckIcon from 'mdi-react/CheckCircleOutlineIcon';
import OpenInNewIcon from 'mdi-react/OpenInNewIcon';
import { _axiosCall } from '../helpers/apicalls';
import { _formatNumber } from '../helpers/utils';
import {
	_fetchPiedATerreRoll,
	_getPiedATerreStatus,
	PAT_LAW_URL,
	_patCheckUrl,
	PAT_REPORT_URL,
	PAT_EFFECTIVE_START,
	PAT_EFFECTIVE_END,
	PAT_PHASE_1_END,
	PAT_APPLICATION_DEADLINE,
	PAT_EXEMPTIONS,
} from '../helpers/piedATerre';

const apiURL = process.env.API_ROOT + '/query/';

const formatRate = rate => `${parseFloat((rate * 100).toFixed(4))}%`;

const shortDollars = v => (v % 1000000 === 0 ? `$${v / 1000000}M` : `$${_formatNumber(v)}`);

const formatBand = bracket => (bracket.max === null
	? `Over ${shortDollars(bracket.min)}`
	: `${shortDollars(bracket.min)} – ${shortDollars(bracket.max)}`);

/** `flag` marks the value that put the property over the threshold. */
const Stat = ({ label, value, caption, flag }) => (
	<div className="pied-a-terre__stat">
		<span className="pied-a-terre__stat-label">{label}</span>
		<span className={`pied-a-terre__stat-value${flag ? ' pied-a-terre__stat-value--flag' : ''}`}>{value}</span>
		{caption && <span className="pied-a-terre__stat-caption">{caption}</span>}
	</div>
);

/**
 * Taxes-tab panel spelling out whether a property is subject to the pied-à-terre
 * surcharge and, when it is, what it would cost. Values come from the DOF market
 * value on `supplemental_tax_roll` — see helpers/piedATerre.js for the rules.
 *
 * `standalone` says this panel is the only thing on the tab (co-op units, which have
 * no unit-level DOF assessment), so a missing roll row has to say so rather than
 * render nothing.
 */
export default function PiedATerreSurcharge({ property, annualTax, standalone }) {
	const [status, setStatus] = useState(null);
	const [loaded, setLoaded] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if(!property || !property.key){
			setStatus(false);
			setLoaded(true);
			return undefined;
		}
		_fetchPiedATerreRoll(_axiosCall, apiURL, property).then(({ roll, isCoopUnit }) => {
			if(cancelled) return;
			setStatus(roll ? _getPiedATerreStatus(property, roll, isCoopUnit) : false);
			setLoaded(true);
		});
		return () => { cancelled = true; };
	}, [property && property.key]);

	if(!status){
		if(!standalone || !loaded) return null;
		return (
			<Row>
				<Col md={12}>
					<Card>
						<CardBody>
							<div className="card__title"><h5 className="bold-text">Pied-à-Terre Surcharge</h5></div>
							<p className="small-text light-text">
								No DOF market value on record for this property, so the surcharge cannot be evaluated.
							</p>
						</CardBody>
					</Card>
				</Col>
			</Row>
		);
	}

	const { tier, meetsThreshold, covered, fmv, taxYear, estimated, estimateBasis, classCaveat } = status;

	return (
		<Row>
			<Col md={12}>
				<Card>
					<CardBody>
						<div className="card__title pied-a-terre__title">
							<h5 className="bold-text">Pied-à-Terre Surcharge</h5>
							<span className={`pied-a-terre__status pied-a-terre__status--${meetsThreshold ? 'subject' : 'not-subject'}`}>
								{meetsThreshold ? 'Meets Value Threshold' : 'Below Value Threshold'}
							</span>
						</div>

						{!covered ? (
							<p className="small-text light-text pied-a-terre__note">{status.note}</p>
						) : (
							<Fragment>
								{/* Value is only one of the tests. Say so before the dollar figure, so the
								    number is never read as a bill the owner definitely owes. */}
								<p className="pied-a-terre__lede small-text">
									{meetsThreshold
										? <Fragment>Meets the value threshold, but is <b>not automatically subject to the surcharge</b> — that depends on the exemptions below.</Fragment>
										: 'Below the value threshold, so the surcharge does not apply.'
									}
								</p>
								<Row>
									<Col md={6}>
										<div className={`pied-a-terre__amount-box${meetsThreshold ? ' is-subject' : ''}`}>
											<span className="pied-a-terre__stat-label">
												{meetsThreshold ? 'Estimated surcharge if not exempt' : 'Below the threshold'}
											</span>
											<span className="pied-a-terre__amount">
												${_formatNumber(status.amount)}<span> / yr</span>
											</span>
											<span className="pied-a-terre__stat-caption">
												{meetsThreshold
													? `$${_formatNumber(Math.round(status.amount / 12))} / mo${annualTax > 0 ? ` — on top of $${_formatNumber(Math.round(annualTax))} in property tax` : ''}`
													: `$${_formatNumber(status.shortfall)} below the ${shortDollars(tier.threshold)} ${tier.label.toLowerCase()} threshold`
												}
											</span>
										</div>
										<div className="pied-a-terre__stats">
											<Stat
												label={`DOF market value${taxYear ? ` (${taxYear})` : ''}`}
												value={`$${_formatNumber(fmv)}${estimated ? '*' : ''}`}
												flag={meetsThreshold}
												caption={meetsThreshold
													? `Over the ${shortDollars(tier.threshold)} ${tier.label.toLowerCase()} threshold`
													: status.unitLevel ? 'DOF per-unit valuation' : estimated ? 'Estimated unit share' : null
												}
											/>
											<Stat label="Rate applied" value={meetsThreshold ? formatRate(status.rate) : 'Does Not Apply'} />
										</div>
									</Col>
									<Col md={6}>
										<table className="pied-a-terre__rates">
											<thead>
												<tr>
													<th>{tier.label} — DOF market value</th>
													<th>Rate</th>
												</tr>
											</thead>
											<tbody>
												<tr className={meetsThreshold ? '' : 'is-active'}>
													<td>Under {shortDollars(tier.threshold)}</td>
													<td>$0</td>
												</tr>
												{tier.brackets.map((bracket, i) => (
													<tr key={i} className={status.bracket && status.bracket.min === bracket.min ? 'is-active' : ''}>
														<td>{formatBand(bracket)}</td>
														<td>{formatRate(bracket.rate)}</td>
													</tr>
												))}
											</tbody>
										</table>
										<p className="small-text light-text pied-a-terre__note">
											Charged on the full market value once the threshold is crossed, not on the amount over it.
											{tier.key === 'ccc' && ' Phase 2 (2028) revalues condos and co-ops to comparable sales, which can shift the bracket.'}
										</p>
									</Col>
								</Row>

								<div className="pied-a-terre__exemptions">
									<div className="pied-a-terre__exemptions-head">
										<AlertIcon size={16} />
										<span className="pied-a-terre__exemptions-title">Exempt from surcharge if:</span>
										<span className="pied-a-terre__deadline-chip">File by {PAT_APPLICATION_DEADLINE}</span>
									</div>
									<div className="pied-a-terre__exemption-list">
										{PAT_EXEMPTIONS.map((exemption, i) => (
											<div className="pied-a-terre__exemption" key={i}>
												<CheckIcon size={14} />
												<span>{exemption}</span>
											</div>
										))}
									</div>
								</div>

								{/* Sells the portfolio report, not the single-property check — this panel already IS
								    the single check, so pointing at the checker would offer what the reader just read. */}
								<div className="pied-a-terre__cta">
									<div className="pied-a-terre__cta-copy">
										<span className="pied-a-terre__cta-title">Screen your whole client list, not one unit</span>
										<span className="pied-a-terre__cta-sub">
											A portfolio exposure report ranks every property you manage by estimated surcharge,
											flags Phase 2 watch-list candidates, and monitors DOF determinations and deadlines.
										</span>
									</div>
									<div className="pied-a-terre__cta-actions">
										<a
											className="btn btn-primary pied-a-terre__cta-btn"
											href={PAT_REPORT_URL}
											target="_blank"
											rel="noopener noreferrer"
										>
											Get Your Exposure Report
											<OpenInNewIcon size={15} />
										</a>
										<a className="link-text pied-a-terre__cta-link" href={PAT_LAW_URL} target="_blank" rel="noopener noreferrer">
											Read the tax law
										</a>
									</div>
								</div>
							</Fragment>
						)}

						<div className="pied-a-terre__footnotes small-text light-text">
							{estimated && (
								<p>
									*Estimated — the co-op&apos;s ${_formatNumber(status.buildingFmv)} value split
									{estimateBasis && estimateBasis.basis === 'shares'
										? ` by this unit's ${estimateBasis.shares}% share`
										: estimateBasis && estimateBasis.units
											? ` evenly across ${estimateBasis.units} units`
											: ''
									}.
								</p>
							)}
							{classCaveat && (
								<p>
									Tax class {status.taxClass} — the surcharge covers 1–3 family homes, so check the unit count.
								</p>
							)}
							{/* The links that used to live here are now the CTA above. */}
							<p>
								In effect {PAT_EFFECTIVE_START} – {PAT_EFFECTIVE_END}; rates above run through {PAT_PHASE_1_END}.
								{!covered && <Fragment>{' '}
									<a className="link-text" href={_patCheckUrl(property)} target="_blank" rel="noopener noreferrer">Run a free check</a>.
								</Fragment>}
							</p>
						</div>
					</CardBody>
				</Card>
			</Col>
		</Row>
	);
}
